import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  CardGiftcard as GiftIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  CheckCircle as CheckCircleIcon,
  QrCode2 as QrCodeIcon,
  ContentCopy as CopyIcon,
  FlightTakeoff as HolidayIcon,
  MonetizationOn as MoneyIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  Verified as VerifiedIcon,
  Stars as StarsIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  School as SchoolIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import API from "../../api/api";
import PremiumScreenHeader from "../../components/common/PremiumScreenHeader";
import BottomNav from "../../components/common/BottomNav";
import { downloadAgentTrainingVoucher } from "../../utils/voucherDownload";

// Simple QR code renderer using standard SVG/canvas or QR code API
function QRCodeView({ value, size = 180 }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    value
  )}&bgcolor=ffffff&color=0f172a&margin=1`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "#ffffff",
        borderRadius: 3,
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
        border: "1px solid #e2e8f0",
      }}
    >
      <img
        src={qrUrl}
        alt="SPP QR Code"
        style={{ width: size, height: size, borderRadius: 8 }}
      />
    </Box>
  );
}

export default function SPPGiftCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedSeason, setSelectedSeason] = useState(1);

  // Modals & Snackbars
  const [qrModalCard, setQrModalCard] = useState(null);
  const [redeemModalCard, setRedeemModalCard] = useState(null);
  const [redeemTripName, setRedeemTripName] = useState("Tri Kerala Premium Tour");
  const [redeeming, setRedeeming] = useState(false);
  const [claimingMaturity, setClaimingMaturity] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const fetchGiftCards = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await API.get("/business/spp/gift-cards/", {
        params: { season: selectedSeason },
      });
      setCards(res.data.results || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error("Failed to fetch SPP gift cards:", err);
      setToast({
        open: true,
        msg: "Failed to load SPP gift cards. Please retry.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchGiftCards();
  }, [selectedSeason]);

  const handleCopy = (code) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code);
      setToast({ open: true, msg: `Copied "${code}" to clipboard!`, severity: "success" });
    }
  };

  const handleRedeemHoliday = async () => {
    if (!redeemModalCard) return;
    setRedeeming(true);
    try {
      const res = await API.post("/business/spp/gift-cards/redeem-holiday/", {
        coupon_code: redeemModalCard.coupon_code,
        trip_id: `TRIP-${redeemTripName.replace(/\s+/g, "-").toUpperCase()}`,
        trip_name: redeemTripName,
      });
      setToast({
        open: true,
        msg: res.data.message || "Gift card successfully redeemed for Tri Holiday!",
        severity: "success",
      });
      setRedeemModalCard(null);
      fetchGiftCards();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || "Redemption failed.";
      setToast({ open: true, msg: errMsg, severity: "error" });
    } finally {
      setRedeeming(false);
    }
  };

  const handleClaimAnnualMaturity = async () => {
    setClaimingMaturity(true);
    try {
      const res = await API.post("/business/spp/gift-cards/claim-maturity/", {
        season_number: selectedSeason,
      });
      setToast({
        open: true,
        msg: res.data.message || "₹14,000 credited to your Main Wallet!",
        severity: "success",
      });
      fetchGiftCards();
    } catch (err) {
      const errMsg = err?.response?.data?.detail || "Annual maturity claim failed.";
      setToast({ open: true, msg: errMsg, severity: "error" });
    } finally {
      setClaimingMaturity(false);
    }
  };

  const filteredCards = useMemo(() => {
    if (statusFilter === "ALL") return cards;
    return cards.filter((c) => c.status === statusFilter);
  }, [cards, statusFilter]);

  const annualMaturity = summary?.annual_maturity;
  const boxesCompleted = annualMaturity?.boxes_completed || 0;
  const progressPercent = Math.min(100, Math.round((boxesCompleted / 13) * 100));

  const filterTabs = [
    { label: "All Vouchers", value: "ALL", count: cards.length },
    { label: "Active (Redeemable)", value: "ACTIVE", count: summary?.active_count || 0 },
    { label: "Locked (0-60 Days)", value: "LOCKED", count: summary?.locked_count || 0 },
    { label: "Maturity Eligible", value: "MATURITY_ELIGIBLE", count: summary?.maturity_eligible_count || 0 },
    { label: "Redeemed for Trips", value: "REDEEMED", count: summary?.redeemed_count || 0 },
    { label: "Matured & Paid", value: "MATURED_PAID", count: summary?.matured_paid_count || 0 },
  ];

  return (
    <Box
      sx={{
        bgcolor: "#F4F7FC",
        minHeight: "100dvh",
        position: "relative",
        pb: { xs: 12, sm: 14 },
      }}
    >
      <PremiumScreenHeader
        title="SPP Gift Cards & Maturity Vault"
        onBack={() => navigate(-1)}
        onNotifications={() => {
          try {
            window.dispatchEvent(new CustomEvent("trikonekt:open-consumer-sidebar"));
          } catch (_) {}
        }}
        onSecondary={() => navigate("/user/coupon-pocket")}
        hasBackdrop={true}
      />

      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          px: { xs: 2, sm: 2.5 },
          pt: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* HERO CARD - Deep Navy/Cobalt with Gold/Gift theme */}
        <Card
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 2.5 },
            borderRadius: "22px",
            mb: 2,
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(135deg, #07152E 0%, #091E3A 40%, #0256B4 100%)",
            color: "#FFFFFF",
            boxShadow: "0 14px 34px -8px rgba(2, 86, 180, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  bgcolor: "rgba(245, 158, 11, 0.16)",
                  border: "1px solid rgba(245, 158, 11, 0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(8px)",
                }}
              >
                <StarsIcon sx={{ fontSize: 24, color: "#FBBF24" }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    fontWeight: 700,
                    letterSpacing: "0.8px",
                    color: "#94A3B8",
                    textTransform: "uppercase",
                  }}
                >
                  SPP GIFT CARDS & MATURITY VAULT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.75)",
                    mt: 0.2,
                  }}
                >
                  Universal vouchers & ₹14,000 maturity savings
                </Typography>
              </Box>
            </Stack>

            {/* 3D Gold Accent */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "radial-gradient(circle at 35% 35%, #F59E0B, #B45309)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
              }}
            >
              <GiftIcon sx={{ fontSize: 26, color: "#FFFFFF" }} />
            </Box>
          </Stack>
        </Card>

      {/* Financial Overview Metrics (2x2 Grid on Mobile, 4-col on Desktop) */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{ mb: 2.5 }}>
        {/* Total SPP Boxes */}
        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #0c1a2e 0%, #162540 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(12, 26, 46, 0.35)",
              border: "1px solid rgba(56, 189, 248, 0.25)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>
                Total Boxes
              </Typography>
              <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: "rgba(56, 189, 248, 0.15)" }}>
                <ShoppingBagIcon sx={{ color: "#38bdf8", fontSize: { xs: 16, sm: 18 } }} />
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.3rem", sm: "1.6rem" }, color: "#ffffff", lineHeight: 1.2 }}>
                  {summary?.total_purchased || 0}{" "}
                  <Typography component="span" sx={{ fontSize: { xs: 11, sm: 13 }, color: "#94a3b8", fontWeight: 600 }}>
                    / 13
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: { xs: 10.5, sm: 11.5 }, color: "#38bdf8", fontWeight: 700, mt: 0.25 }}>
                  ₹{(summary?.total_invested || 0).toLocaleString("en-IN")} Value
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 18, mb: 0.2 }} />
            </Box>
          </Card>
        </Grid>

        {/* Active Universal Vouchers */}
        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #064e3b 0%, #047857 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(4, 120, 87, 0.35)",
              border: "1px solid rgba(52, 211, 153, 0.3)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#a7f3d0", textTransform: "uppercase" }}>
                Active Vouchers
              </Typography>
              <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.18)" }}>
                <GiftIcon sx={{ color: "#6ee7b7", fontSize: { xs: 16, sm: 18 } }} />
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.3rem", sm: "1.6rem" }, color: "#ffffff", lineHeight: 1.2 }}>
                  {summary?.active_count || 0}{" "}
                  <Typography component="span" sx={{ fontSize: { xs: 11, sm: 13 }, color: "#a7f3d0", fontWeight: 600 }}>
                    Active
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: { xs: 10.5, sm: 11.5 }, color: "#a7f3d0", fontWeight: 700, mt: 0.25 }}>
                  ₹{((summary?.active_count || 0) * 1000).toLocaleString("en-IN")} Balance
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18, mb: 0.2 }} />
            </Box>
          </Card>
        </Grid>

        {/* Locked for 60 Days */}
        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #78350f 0%, #92400e 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(120, 53, 15, 0.35)",
              border: "1px solid rgba(251, 191, 36, 0.3)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#fde68a", textTransform: "uppercase" }}>
                60-Day Lock
              </Typography>
              <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.18)" }}>
                <LockIcon sx={{ color: "#fde68a", fontSize: { xs: 16, sm: 18 } }} />
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.3rem", sm: "1.6rem" }, color: "#ffffff", lineHeight: 1.2 }}>
                  {summary?.locked_count || 0}{" "}
                  <Typography component="span" sx={{ fontSize: { xs: 11, sm: 13 }, color: "#fde68a", fontWeight: 600 }}>
                    Locked
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: { xs: 10.5, sm: 11.5 }, color: "#fde68a", fontWeight: 700, mt: 0.25 }}>
                  Unlocks in 60d
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18, mb: 0.2 }} />
            </Box>
          </Card>
        </Grid>

        {/* Annual Maturity Status */}
        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)",
              color: "#ffffff",
              boxShadow: "0 8px 24px rgba(76, 29, 149, 0.35)",
              border: "1px solid rgba(192, 132, 252, 0.35)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#e9d5ff", textTransform: "uppercase" }}>
                Annual Maturity
              </Typography>
              <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.18)" }}>
                <StarsIcon sx={{ color: "#f0abfc", fontSize: { xs: 16, sm: 18 } }} />
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.3rem", sm: "1.6rem" }, color: "#ffffff", lineHeight: 1.2 }}>
                  ₹14,000
                </Typography>
                <Typography sx={{ fontSize: { xs: 10.5, sm: 11.5 }, color: "#e9d5ff", fontWeight: 700, mt: 0.25 }}>
                  ₹12k + ₹2k Bonus
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18, mb: 0.2 }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* 30-Day Renewal Cadence Countdown Card */}
      {summary?.renewal_cadence && (
        <Card
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            border: "1.5px solid",
            borderColor:
              summary.renewal_cadence.cadence_status === "OVERDUE"
                ? "#fca5a5"
                : summary.renewal_cadence.cadence_status === "DUE_TODAY"
                ? "#fed7aa"
                : summary.renewal_cadence.cadence_status === "DUE_SOON"
                ? "#fde047"
                : "#bbf7d0",
            bgcolor:
              summary.renewal_cadence.cadence_status === "OVERDUE"
                ? "#fff5f5"
                : summary.renewal_cadence.cadence_status === "DUE_TODAY"
                ? "#fffaf0"
                : "#f0fdf4",
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Box sx={{ width: "100%" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Chip
                  size="small"
                  label={
                    summary.renewal_cadence.cadence_status === "COMPLETED"
                      ? "Maturity Complete"
                      : summary.renewal_cadence.cadence_status === "OVERDUE"
                      ? "Renewal Overdue"
                      : summary.renewal_cadence.cadence_status === "DUE_TODAY"
                      ? "Due Today"
                      : summary.renewal_cadence.cadence_status === "DUE_SOON"
                      ? "Due Soon"
                      : "Streak On Track"
                  }
                  sx={{
                    fontWeight: 800,
                    fontSize: "11px",
                    bgcolor:
                      summary.renewal_cadence.cadence_status === "OVERDUE"
                        ? "#dc2626"
                        : summary.renewal_cadence.cadence_status === "DUE_TODAY"
                        ? "#ea580c"
                        : summary.renewal_cadence.cadence_status === "DUE_SOON"
                        ? "#ca8a04"
                        : "#16a34a",
                    color: "#fff",
                  }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  30-Day Monthly Renewal Term
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "13px" }}>
                {summary.renewal_cadence.status_message}
              </Typography>
              {summary.renewal_cadence.next_purchase_date && (
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, display: "block", mt: 0.5 }}>
                  📅 Next Scheduled: <b>{summary.renewal_cadence.next_purchase_date}</b> • Last Box: {summary.renewal_cadence.last_purchase_date || "N/A"}
                </Typography>
              )}
            </Box>
          </Stack>
        </Card>
      )}

      {/* Annual Maturity Vault Progress Card */}
      <Card
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: 3,
          borderRadius: 3.5,
          bgcolor: "#ffffff",
          border: "1.5px solid #e2e8f0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <MoneyIcon sx={{ color: "#8b5cf6", fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                13-Month SPP Maturity Vault Progress (Season {selectedSeason})
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "13px" }}>
              Complete 12 monthly subscription boxes timely (total 13 boxes) to receive <b>2 Company Bonus Vouchers of ₹2,000 each (Total 15 Vouchers)</b> at the end of the year for Trikonekt Shopping & Merchant stores!
            </Typography>
          </Box>

          {annualMaturity?.status === "READY_TO_CLAIM" ? (
            <Button
              variant="contained"
              fullWidth
              onClick={handleClaimAnnualMaturity}
              disabled={claimingMaturity}
              startIcon={<StarsIcon />}
              sx={{
                background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                color: "#fff",
                fontWeight: 900,
                fontSize: "14px",
                px: 3,
                py: 1.2,
                borderRadius: 2.5,
                textTransform: "none",
                boxShadow: "0 6px 20px rgba(139,92,246,0.35)",
                whiteSpace: "nowrap",
                maxWidth: { md: 280 },
              }}
            >
              {claimingMaturity ? "Processing..." : "Claim ₹14,000 Payout"}
            </Button>
          ) : annualMaturity?.status === "MATURED_PAID" ? (
            <Chip
              label="₹14,000 Payout Paid to Wallet"
              color="success"
              sx={{ fontWeight: 800, px: 1.5, py: 2, fontSize: 12.5, borderRadius: 2 }}
            />
          ) : (
            <Chip
              label={`${boxesCompleted}/13 Boxes Completed`}
              sx={{
                bgcolor: "#f1f5f9",
                color: "#475569",
                fontWeight: 800,
                fontSize: 12.5,
                px: 1,
                py: 1.75,
                borderRadius: 2,
              }}
            />
          )}
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#475569" }}>
              Annual Savings Progress: {boxesCompleted} of 13 Months
            </Typography>
            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#8b5cf6" }}>
              {progressPercent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                background: "linear-gradient(90deg, #8b5cf6 0%, #10b981 100%)",
              },
            }}
          />
        </Box>
      </Card>

      {/* Modern Horizontal Scroll Filter Pill Tabs (Fixed Overlapping) */}
      <Box
        sx={{
          mb: 2.5,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
          pt: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {filterTabs.map((tab) => {
          const isSelected = statusFilter === tab.value;
          return (
            <Button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              disableElevation
              sx={{
                flexShrink: 0,
                minWidth: "fit-content",
                px: { xs: 1.75, sm: 2.25 },
                py: 0.75,
                borderRadius: "999px",
                fontSize: { xs: "12px", sm: "13px" },
                fontWeight: 700,
                textTransform: "none",
                whiteSpace: "nowrap",
                bgcolor: isSelected ? "#0f172a" : "#ffffff",
                color: isSelected ? "#ffffff" : "#475569",
                border: isSelected ? "1.5px solid #0f172a" : "1.5px solid #e2e8f0",
                boxShadow: isSelected ? "0 4px 12px rgba(15,23,42,0.18)" : "0 1px 3px rgba(0,0,0,0.03)",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: isSelected ? "#1e293b" : "#f1f5f9",
                  borderColor: isSelected ? "#1e293b" : "#cbd5e1",
                },
              }}
            >
              {tab.label}
              <Box
                component="span"
                sx={{
                  ml: 0.8,
                  px: 0.85,
                  py: 0.15,
                  borderRadius: "999px",
                  bgcolor: isSelected ? "rgba(255,255,255,0.22)" : "#f1f5f9",
                  color: isSelected ? "#ffffff" : "#64748b",
                  fontSize: "11px",
                  fontWeight: 800,
                  display: "inline-block",
                }}
              >
                {tab.count}
              </Box>
            </Button>
          );
        })}
      </Box>

      {/* Gift Cards Grid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={40} sx={{ color: "#2563eb" }} />
        </Box>
      ) : filteredCards.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            borderRadius: 3.5,
            bgcolor: "#f8fafc",
            border: "1.5px dashed #cbd5e1",
          }}
        >
          <GiftIcon sx={{ fontSize: 44, color: "#94a3b8", mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#334155", mb: 0.75 }}>
            No SPP Gift Cards in this category
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, maxWidth: 440, mx: "auto", fontSize: "13px" }}>
            SPP monthly subscriptions and shopping vouchers are available directly on asiyapp.com.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/user/rank-upgrade")}
            sx={{
              borderColor: "#2563eb",
              color: "#2563eb",
              fontWeight: 700,
              borderRadius: 2.5,
              textTransform: "none",
              px: 3,
            }}
          >
            Explore Packages on asiyapp.com ↗
          </Button>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {filteredCards.map((card) => {
            const isLocked = card.status === "LOCKED";
            const isActive = card.status === "ACTIVE";
            const isRedeemed = card.status === "REDEEMED";
            const isMaturityEligible = card.status === "MATURITY_ELIGIBLE";
            const isMaturedPaid = card.status === "MATURED_PAID";

            // Agent Training Voucher Logic for Month 1 Box
            const isAgentTraining = Number(card.box_number) === 1;
            const isAgentTrainingActivated =
              isAgentTraining &&
              (card.agent_training_status === "ACTIVATED" ||
                (Number(card.user_rank_level || 0) >= 7 && Number(card.direct_sponsors_count || 0) >= 5));
            const isAgentTrainingInactive = isAgentTraining && !isAgentTrainingActivated;

            // Status border & background themes
            let cardBg = "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)";
            let accentColor = "#38bdf8";

            if (isAgentTraining) {
              if (isAgentTrainingActivated) {
                cardBg = "linear-gradient(135deg, #064e3b 0%, #065f46 100%)";
                accentColor = "#34d399";
              } else {
                cardBg = "linear-gradient(135deg, #3f2d1d 0%, #1c150c 100%)";
                accentColor = "#f59e0b";
              }
            } else if (isActive) {
              cardBg = "linear-gradient(135deg, #064e3b 0%, #022c22 100%)";
              accentColor = "#10b981";
            } else if (isLocked) {
              cardBg = "linear-gradient(135deg, #3f2d1d 0%, #1c150c 100%)";
              accentColor = "#f59e0b";
            } else if (isMaturedPaid || isMaturityEligible) {
              cardBg = "linear-gradient(135deg, #3b0764 0%, #1e1b4b 100%)";
              accentColor = "#c084fc";
            }

            return (
              <Grid item xs={12} sm={6} lg={4} key={card.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3.5,
                    background: cardBg,
                    color: "#ffffff",
                    border: `1.5px solid ${accentColor}44`,
                    boxShadow: `0 8px 24px rgba(0,0,0,0.2)`,
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: 280,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: `0 14px 32px rgba(0,0,0,0.3)`,
                    },
                  }}
                >
                  {/* Decorative Shimmer / Watermark */}
                  <Box
                    sx={{
                      position: "absolute",
                      right: -15,
                      bottom: -15,
                      opacity: 0.08,
                      pointerEvents: "none",
                    }}
                  >
                    <GiftIcon sx={{ fontSize: 160, color: "#ffffff" }} />
                  </Box>

                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, position: "relative", zIndex: 1 }}>
                    {/* Top Row: Brand & Box Number */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 9.5,
                            fontWeight: 900,
                            letterSpacing: 1.2,
                            color: accentColor,
                            textTransform: "uppercase",
                          }}
                        >
                          {isAgentTraining ? "AGENT TRAINING VOUCHER" : "ASIYAPP SPP VOUCHER"}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: "#ffffff", letterSpacing: -0.5, fontSize: "1.4rem" }}>
                          ₹{Number(card.amount || 1000).toLocaleString("en-IN")}
                        </Typography>
                      </Box>

                      <Chip
                        label={
                          isAgentTraining
                            ? isAgentTrainingActivated
                              ? "Month 1 • Agent Training (Active)"
                              : "Month 1 • Agent Training (Inactive)"
                            : `Month ${card.box_number} Box`
                        }
                        sx={{
                          bgcolor: isAgentTraining
                            ? isAgentTrainingActivated
                              ? "rgba(16, 185, 129, 0.25)"
                              : "rgba(245, 158, 11, 0.25)"
                            : "rgba(255,255,255,0.12)",
                          color: isAgentTraining
                            ? isAgentTrainingActivated
                              ? "#34d399"
                              : "#fbbf24"
                            : "#ffffff",
                          fontWeight: 800,
                          fontSize: 10.5,
                          borderRadius: 2,
                          border: `1px solid ${
                            isAgentTraining
                              ? isAgentTrainingActivated
                                ? "rgba(16, 185, 129, 0.5)"
                                : "rgba(245, 158, 11, 0.5)"
                              : "rgba(255,255,255,0.2)"
                          }`,
                        }}
                      />
                    </Stack>

                    {/* Voucher Code Box */}
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.35)",
                        border: "1px dashed rgba(255,255,255,0.2)",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
                          Voucher Code (Phone: {card.user_phone || "User"})
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 800,
                            letterSpacing: 0.8,
                            color: "#ffffff",
                            fontFamily: "monospace",
                          }}
                        >
                          {card.coupon_code}
                        </Typography>
                      </Box>

                      <Tooltip title="Copy Code">
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(card.coupon_code)}
                          sx={{
                            color: accentColor,
                            bgcolor: "rgba(255,255,255,0.08)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Agent Training Qualification Status Banner */}
                    {isAgentTraining && isAgentTrainingInactive && (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(245, 158, 11, 0.14)",
                          border: "1px solid rgba(245, 158, 11, 0.35)",
                          color: "#fde68a",
                          mb: 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                          <LockIcon sx={{ fontSize: 15, color: "#f59e0b" }} />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "#fbbf24" }}>
                            Inactive • Qualification Pending
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 10.5, color: "#fef3c7", lineHeight: 1.35 }}>
                          Requires <b>Rank L7+</b> & <b>5 Active Directs</b>.
                          <br />
                          Current: <b>Rank L{card.user_rank_level || 0}</b> •{" "}
                          <b>{card.direct_sponsors_count || 0}/5 Directs</b>
                        </Typography>
                      </Box>
                    )}

                    {isAgentTraining && isAgentTrainingActivated && (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(16, 185, 129, 0.16)",
                          border: "1px solid rgba(16, 185, 129, 0.4)",
                          color: "#a7f3d0",
                          mb: 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                          <VerifiedIcon sx={{ fontSize: 15, color: "#34d399" }} />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "#34d399" }}>
                            ⚡ Activated • Qualified (100% Usable)
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 10.5, color: "#d1fae5" }}>
                          Rank L{card.user_rank_level || 7} Achieved • {card.direct_sponsors_count || 5} Active Directs
                        </Typography>
                      </Box>
                    )}

                    {/* Agent Training Educational Scope Tags */}
                    {isAgentTraining && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1, mt: 0.5 }}>
                        <Chip
                          size="small"
                          label="✓ Agent Training Only"
                          sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#ffffff", fontSize: 10, fontWeight: 700, borderRadius: 1.5 }}
                        />
                        <Chip
                          size="small"
                          label="✓ LMS Modules"
                          sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#ffffff", fontSize: 10, fontWeight: 700, borderRadius: 1.5 }}
                        />
                        <Chip
                          size="small"
                          label="✓ Certification & Workshops"
                          sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#ffffff", fontSize: 10, fontWeight: 700, borderRadius: 1.5 }}
                        />
                      </Stack>
                    )}

                    {/* Standard Non-Agent Training Status & Validity Info */}
                    {!isAgentTraining && isLocked && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#fde68a", mb: 0.5 }}>
                        <LockIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                          Locked for 60 Days ({card.days_until_unlock}d left • Unlocks {dayjs(card.unlock_at).format("DD MMM YYYY")})
                        </Typography>
                      </Stack>
                    )}

                    {!isAgentTraining && isActive && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#6ee7b7", mb: 0.5 }}>
                        <UnlockIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                          Active (100% Usable • {card.days_until_expiry}d left • Valid until {dayjs(card.expires_at).format("DD MMM YYYY")})
                        </Typography>
                      </Stack>
                    )}

                    {isRedeemed && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#93c5fd", mb: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                          Redeemed for: {card.redeemed_trip_name || "Tri Holiday Tour"}
                        </Typography>
                      </Stack>
                    )}

                    {isMaturityEligible && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#f0abfc", mb: 0.5 }}>
                        <StarsIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                          Saved for Year-End Maturity (₹12k + ₹2k Bonus)
                        </Typography>
                      </Stack>
                    )}

                    {isMaturedPaid && (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#86efac", mb: 0.5 }}>
                        <VerifiedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                          ₹14,000 Annual Maturity Paid to Main Wallet
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>

                  {/* Actions Bar */}
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      pt: 0,
                      display: "flex",
                      gap: 1,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {/* View QR - ONLY for Non-Agent Training SPP Vouchers (Months 2-13) */}
                    {!isAgentTraining && (
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<QrCodeIcon />}
                        onClick={() => setQrModalCard(card)}
                        sx={{
                          color: "#ffffff",
                          borderColor: "rgba(255,255,255,0.25)",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: 2,
                          textTransform: "none",
                          "&:hover": {
                            borderColor: "#ffffff",
                            bgcolor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        View QR
                      </Button>
                    )}

                    {/* Agent Training: Copy Code button */}
                    {isAgentTraining && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopy(card.coupon_code)}
                        sx={{
                          color: "#ffffff",
                          borderColor: "rgba(255,255,255,0.25)",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: 2,
                          textTransform: "none",
                          minWidth: 115,
                          "&:hover": {
                            borderColor: "#ffffff",
                            bgcolor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        Copy Code
                      </Button>
                    )}

                    {/* Agent Training Inactive (Locked) */}
                    {isAgentTrainingInactive && (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        disabled
                        startIcon={<LockIcon />}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.08) !important",
                          color: "#94a3b8 !important",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: 2,
                          textTransform: "none",
                          border: "1px dashed rgba(255,255,255,0.2)",
                        }}
                      >
                        🔒 Locked (L7 & 5 Directs)
                      </Button>
                    )}

                    {/* Agent Training Activated: Download Voucher & Directs user to Agent Training on trieducation.in */}
                    {isAgentTraining && isAgentTrainingActivated && (
                      <Stack spacing={1} sx={{ width: "100%" }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => {
                            downloadAgentTrainingVoucher({
                              couponCode: card.coupon_code,
                              userName: card.user_name || card.user_phone || "Certified Agent",
                              userPhone: card.user_phone || "",
                              rankLevel: card.user_rank_level || 7,
                              directsCount: card.direct_sponsors_count || 5,
                              amount: card.amount || 1000,
                              activatedAt: card.unlock_at || card.purchased_at,
                              pdfUrl: card.pdf_url,
                            });
                          }}
                          sx={{
                            color: "#34d399",
                            borderColor: "rgba(16, 185, 129, 0.5)",
                            bgcolor: "rgba(16, 185, 129, 0.1)",
                            fontWeight: 800,
                            fontSize: "12px",
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": {
                              borderColor: "#10b981",
                              bgcolor: "rgba(16, 185, 129, 0.2)",
                            },
                          }}
                        >
                          Download Voucher
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={<SchoolIcon />}
                          onClick={() => {
                            window.open("https://trieducation.in/education", "_blank") || (window.location.href = "https://trieducation.in/education");
                          }}
                          sx={{
                            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "#0f172a",
                            fontWeight: 900,
                            fontSize: "12px",
                            borderRadius: 2,
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                            },
                          }}
                        >
                          Access Agent Training →
                        </Button>
                      </Stack>
                    )}

                    {/* Normal Active Non-Agent Training Voucher (Month 2 to 13) */}
                    {!isAgentTraining && isActive && (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<HolidayIcon />}
                        onClick={() => setRedeemModalCard(card)}
                        sx={{
                          bgcolor: "#10b981",
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: "12px",
                          borderRadius: 2,
                          textTransform: "none",
                          "&:hover": { bgcolor: "#059669" },
                        }}
                      >
                        Redeem
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* QR Code Dialog */}
      <Dialog
        open={Boolean(qrModalCard)}
        onClose={() => setQrModalCard(null)}
        PaperProps={{
          sx: { borderRadius: 4, maxWidth: 360, width: "90%", p: 1 },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 900, pb: 0.5 }}>
          SPP Voucher QR Code
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          {qrModalCard && (
            <>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2, fontSize: "13px" }}>
                Scan to redeem towards Tri Holiday packages or verify authenticity.
              </Typography>

              <QRCodeView value={qrModalCard.coupon_code} size={180} />

              <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                  Alphanumeric Voucher Code
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 900,
                    fontFamily: "monospace",
                    color: "#0f172a",
                    letterSpacing: 1,
                  }}
                >
                  {qrModalCard.coupon_code}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#2563eb", fontWeight: 600, mt: 0.25 }}>
                  Face Value: ₹1,000 • Month {qrModalCard.box_number} Box
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setQrModalCard(null)}
            sx={{
              bgcolor: "#0f172a",
              "&:hover": { bgcolor: "#1e293b" },
              fontWeight: 700,
              borderRadius: 2,
              px: 4,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redeem for Tri Holiday Dialog */}
      <Dialog
        open={Boolean(redeemModalCard)}
        onClose={() => setRedeemModalCard(null)}
        PaperProps={{
          sx: { borderRadius: 4, maxWidth: 420, width: "92%", p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 0.5 }}>
          Redeem for Tri Holiday
        </DialogTitle>
        <DialogContent>
          {redeemModalCard && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: 2.5, fontSize: "12.5px" }}>
                Applying voucher <b>{redeemModalCard.coupon_code}</b> gives you an instant <b>₹1,000</b> deduction towards your travel package.
              </Alert>

              <TextField
                select
                fullWidth
                size="small"
                label="Select Tri Holiday Destination"
                value={redeemTripName}
                onChange={(e) => setRedeemTripName(e.target.value)}
              >
                <MenuItem value="Tri Kerala Backwaters & Hills (5D/4N)">
                  Tri Kerala Backwaters & Hills (5D/4N)
                </MenuItem>
                <MenuItem value="Tri Thailand Exotic Explorer (6D/5N)">
                  Tri Thailand Exotic Explorer (6D/5N)
                </MenuItem>
                <MenuItem value="Tri Goa Coastal Escape (4D/3N)">
                  Tri Goa Coastal Escape (4D/3N)
                </MenuItem>
                <MenuItem value="Tri Kashmir Paradise Experience (6D/5N)">
                  Tri Kashmir Paradise Experience (6D/5N)
                </MenuItem>
              </TextField>

              <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>Package Discount:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#10b981" }}>- ₹1,000.00</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>Voucher Used:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                    {redeemModalCard.coupon_code}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRedeemModalCard(null)} sx={{ fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRedeemHoliday}
            disabled={redeeming}
            sx={{
              bgcolor: "#10b981",
              "&:hover": { bgcolor: "#059669" },
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
            }}
          >
            {redeeming ? "Redeeming..." : "Confirm Discount"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: 3, fontWeight: 700 }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Mobile Bottom Clearance Spacer */}
      <Box sx={{ height: { xs: 80, sm: 40 } }} />
      </Box>
    </Box>
  );
}
